import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@heroui/react';
import { PhoneIcon, PhoneOffIcon } from '@/components/icons';
import { avatarHue } from '@/lib/avatarHue';
import {
  acceptIncomingCall,
  cancelOutgoingCall,
  getDmCall,
  rejectIncomingCall,
  subscribeDmCall,
  type DmCallState,
} from '@/lib/dmCall';

/**
 * Global DM call-modal layer. Three visual states tied to the
 * dmCall state machine in lib/dmCall.ts:
 *
 *   outgoing  — "Звоним <name>…" with a Cancel button. Caller side.
 *   incoming  — "<name> звонит" with Accept (green) + Reject (red) buttons.
 *   in-call / connecting — hidden here; the regular voice UI in the
 *                          DrawerUserWidget already surfaces these
 *                          (microphone toggle, hangup, etc).
 *
 * Mounted once at app shell level so a ring can pop while the user is
 * anywhere in the app (in a guild channel, in another DM, on settings).
 */
export function DmCallModal() {
  const { t } = useTranslation();
  const [state, setState] = useState<DmCallState>(() => getDmCall());
  useEffect(() => subscribeDmCall(setState), []);

  const showOutgoing = state.kind === 'outgoing';
  const showIncoming = state.kind === 'incoming';
  const open = showOutgoing || showIncoming;

  return (
    <Modal.Backdrop
      isOpen={open}
      variant="blur"
      onOpenChange={(o) => {
        if (!o) {
          // Hardware close (Esc / backdrop) — treat as cancel/reject so
          // server is notified. Without this the server keeps the
          // pending invite around for 60s and the other side keeps
          // ringing for nothing.
          if (showOutgoing) cancelOutgoingCall();
          else if (showIncoming) rejectIncomingCall();
        }
      }}
    >
      <Modal.Container size="sm" placement="center">
        <Modal.Dialog
          aria-label={showIncoming ? t('cmod.call.incomingAria') : t('cmod.call.outgoingAria')}
          className="overflow-hidden p-0"
        >
          {showOutgoing && (
            <OutgoingPanel
              target={state.target}
              onCancel={cancelOutgoingCall}
            />
          )}
          {showIncoming && (
            <IncomingPanel
              caller={state.caller}
              onAccept={acceptIncomingCall}
              onReject={rejectIncomingCall}
            />
          )}
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function OutgoingPanel(props: {
  target: { displayName: string; avatarUrl: string | null };
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-5 px-6 pb-6 pt-7 text-center">
      <CallerAvatar
        displayName={props.target.displayName}
        avatarUrl={props.target.avatarUrl}
      />
      <div className="min-w-0 max-w-full space-y-1">
        <div className="text-xs text-ink-muted">{t('cmod.call.waiting')}</div>
        <div className="truncate font-display text-xl font-semibold text-ink">
          {props.target.displayName}
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={props.onCancel}
          className="grid size-12 place-items-center rounded-full bg-danger text-white transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
          aria-label={t('cmod.call.cancel')}
        >
          <PhoneOffIcon className="size-5" />
        </button>
        <span className="text-xs text-ink-muted">{t('cmod.call.cancel')}</span>
      </div>
    </div>
  );
}

function IncomingPanel(props: {
  caller: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  };
  onAccept: () => void;
  onReject: () => void;
}) {
  const { t } = useTranslation();
  const name = props.caller.displayName || props.caller.username;
  return (
    <div className="flex flex-col items-center gap-5 px-6 pb-6 pt-7 text-center">
      <CallerAvatar
        displayName={name}
        avatarUrl={props.caller.avatarUrl}
      />
      <div className="min-w-0 max-w-full space-y-1">
        <div className="text-xs text-ink-muted">{t('cmod.call.incomingAria')}</div>
        <div className="truncate font-display text-xl font-semibold text-ink">
          {name}
        </div>
        {name !== props.caller.username && (
          <div className="truncate text-xs text-ink-muted">@{props.caller.username}</div>
        )}
      </div>
      <div className="flex items-start gap-8">
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={props.onReject}
            className="grid size-12 place-items-center rounded-full bg-danger text-white transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
            aria-label={t('cmod.call.reject')}
          >
            <PhoneOffIcon className="size-5" />
          </button>
          <span className="text-xs text-ink-muted">{t('cmod.call.reject')}</span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={props.onAccept}
            className="grid size-12 place-items-center rounded-full bg-accent transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            style={{ color: 'var(--accent-foreground)' }}
            aria-label={t('cmod.call.accept')}
          >
            <PhoneIcon className="size-5" />
          </button>
          <span className="text-xs text-ink-muted">{t('cmod.call.accept')}</span>
        </div>
      </div>
    </div>
  );
}

function CallerAvatar(props: {
  displayName: string;
  avatarUrl: string | null;
}) {
  const hue = avatarHue(props.displayName);
  return (
    <div
      className={
        'grid size-[72px] place-items-center overflow-hidden rounded-full font-display text-xl font-medium ' +
        (props.avatarUrl ? 'bg-surface-secondary text-ink' : `${hue.bg} ${hue.text}`)
      }
    >
      {props.avatarUrl ? (
        <img src={props.avatarUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        props.displayName.charAt(0).toUpperCase()
      )}
    </div>
  );
}
